// Mirror of /app/backend/enums.py — keep in sync.
// Whenever a value changes on the backend, update this file and any UI
// components that render the enum directly.

export const STATUSES = ["New", "Working", "Testing", "On Hold", "Blocked", "Done", "Closed"];
export const OPEN_STATUSES = ["New", "Working", "Testing", "On Hold", "Blocked"];

export const PRIORITIES = ["Critical", "Urgent", "High", "Medium", "Low"];

export const TYPES = ["Bug", "Feature", "Improvement", "Chore", "Incident"];

// Wave 2 scoring vocab (mirrors backend). All optional per-item.
export const EFFORTS = ["S", "M", "L"];
export const IMPACTS = [1, 2, 3, 4, 5];
export const PHASES = ["Now", "Phase 1", "Later"];

// Pseudo-assignees — team values pinned above the real user list everywhere.
export const PSEUDO_ASSIGNEES = [
  { id: "emergent", label: "Emergent (EMG)", short: "EMG" },
  { id: "joint",    label: "Joint",          short: "JNT" },
  { id: "gami",     label: "GAMI (Niraj & Raj)", short: "GAMI" },
];
export const PSEUDO_BY_ID = Object.fromEntries(PSEUDO_ASSIGNEES.map((p) => [p.id, p]));

// Lightweight tag on tracker items only — NOT an app-wide toggle.
export const TALMIA_VERSIONS = ["1", "2", "both"];

// Icon glyph + tone per work-type — used inline on tables & chips.
export const TYPE_META = {
  Bug:         { icon: "🐛", tone: "text-red-300 border-red-400/30 bg-red-500/[0.06]" },
  Feature:     { icon: "✨", tone: "text-purple-300 border-purple-400/30 bg-purple-500/[0.06]" },
  Improvement: { icon: "📈", tone: "text-sky-300 border-sky-400/30 bg-sky-500/[0.06]" },
  Chore:       { icon: "🔧", tone: "text-slate-400 border-slate-400/30 bg-slate-500/[0.06]" },
  Incident:    { icon: "🚨", tone: "text-rose-300 border-rose-400/40 bg-rose-500/[0.10]" },
};

export const STATUS_META = {
  New:       { tone: "text-sky-300 border-sky-400/30 bg-sky-500/[0.06]" },
  Working:   { tone: "text-amber-300 border-amber-400/30 bg-amber-500/[0.06]" },
  Testing:   { tone: "text-violet-300 border-violet-400/40 bg-violet-500/[0.10]" },
  "On Hold": { tone: "text-slate-400 border-slate-400/30 bg-slate-500/[0.06]" },
  Blocked:   { tone: "text-rose-300 border-rose-400/30 bg-rose-500/[0.06]" },
  Done:      { tone: "text-emerald-300 border-emerald-400/30 bg-emerald-500/[0.06]" },
  Closed:    { tone: "text-slate-500 border-slate-500/30 bg-slate-500/[0.04]" },
};

export const TALMIA_META = {
  "1":    { label: "T1",   tone: "text-amber-300 border-amber-400/30 bg-amber-500/[0.06]" },
  "2":    { label: "T2",   tone: "text-teal-300 border-teal-400/30 bg-teal-500/[0.06]" },
  both:   { label: "Both", tone: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-500/[0.06]" },
};

// Sort helpers used by the Dashboard's client-side sorter.
export const STATUS_ORDER = { New: 0, Working: 1, Testing: 2, "On Hold": 3, Blocked: 4, Done: 5, Closed: 6 };
export const PRIORITY_ORDER = { Critical: 0, Urgent: 0, High: 1, Medium: 2, Low: 3 };

// Legacy pill-styling maps, still consumed by /components/Badges.jsx.
// Kept as tone triples (bg / text / border) so any variant can be built
// from the same design tokens.
export const STATUS_STYLES = {
  New:       { bg: "bg-sky-500/10",      text: "text-sky-300",      border: "border-sky-400/30" },
  Working:   { bg: "bg-amber-500/10",    text: "text-amber-300",    border: "border-amber-400/30" },
  Testing:   { bg: "bg-violet-500/15",   text: "text-violet-300",   border: "border-violet-400/40" },
  "On Hold": { bg: "bg-slate-500/10",    text: "text-slate-300",    border: "border-slate-400/30" },
  Blocked:   { bg: "bg-rose-500/10",     text: "text-rose-300",     border: "border-rose-400/30" },
  Done:      { bg: "bg-emerald-500/10",  text: "text-emerald-300",  border: "border-emerald-400/30" },
  Closed:    { bg: "bg-slate-500/10",    text: "text-slate-400",    border: "border-slate-500/30" },
};

export const PRIORITY_STYLES = {
  Critical: { bg: "bg-red-500/15",     text: "text-red-300",    border: "border-red-500/40" },
  Urgent:   { bg: "bg-red-500/10",     text: "text-red-300",    border: "border-red-400/30" },
  High:     { bg: "bg-orange-500/10",  text: "text-orange-300", border: "border-orange-400/30" },
  Medium:   { bg: "bg-amber-500/10",   text: "text-amber-300",  border: "border-amber-400/30" },
  Low:      { bg: "bg-sky-500/10",     text: "text-sky-300",    border: "border-sky-400/30" },
};

export const TYPE_STYLES = {
  Bug:         { bg: "bg-red-500/10",     text: "text-red-300",     border: "border-red-400/30" },
  Feature:     { bg: "bg-purple-500/10",  text: "text-purple-300",  border: "border-purple-400/30" },
  Improvement: { bg: "bg-sky-500/10",     text: "text-sky-300",     border: "border-sky-400/30" },
  Chore:       { bg: "bg-slate-500/10",   text: "text-slate-400",   border: "border-slate-500/30" },
  Incident:    { bg: "bg-rose-500/15",    text: "text-rose-300",    border: "border-rose-500/40" },
};

export const TALMIA_STYLES = {
  "1":    { bg: "bg-amber-500/10",   text: "text-amber-300",   border: "border-amber-400/30" },
  "2":    { bg: "bg-teal-500/10",    text: "text-teal-300",    border: "border-teal-400/30" },
  both:   { bg: "bg-fuchsia-500/10", text: "text-fuchsia-300", border: "border-fuchsia-400/30" },
};
